import React, { useEffect, useMemo, useState } from 'react';
import CustomOptions from '@src/components/CustomOptions';
import NewCustomOptions from '@src/components/NewCustomOptions';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';
import { Button, Typography, useMediaQuery } from '@mui/material';
import {
  generateSelectOptions,
  sortCustomResourceKeys,
} from '@src/services/package';
import { checkVietNam } from '@src/utils/checkCountry';
import { LANGUAGE } from '@src/constants';
import { RESOURCES_TYPE } from '@src/constants/package';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import theme from '@src/styles/theme';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useLocation from '@src/hooks/useLocation';
import { StyledCustomResources } from './index.styles';

const CustomResources = ({ pkg, onPriceChange, onInitializePrices }) => {
  const { i18n, t } = useTranslation();
  const { language } = i18n;
  const { customResource = {}, note } = pkg || {};
  const isVietNam = checkVietNam();
  const [openPopup, setOpenPopup] = useState(false);
  const [tooltipDetail, setTooltipDetail] = useState('');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { ip } = useLocation();
  const { getFeatureValue } = useFeatureFlags();
  const mergePackageApiToStudioEnabled = getFeatureValue(
    FEATURE_KEYS.MERGE_PACKAGE_API_TO_STUDIO,
    {
      ip,
    },
  );

  const toKebabCase = (str) =>
    str
      .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert dash between camelCase
      .toLowerCase();

  const customResources = useMemo(() => {
    const sortedKeys = sortCustomResourceKeys(customResource);

    return sortedKeys.map((key) => {
      const { prices } = customResource[key];
      const { step } = customResource[key];
      const options = generateSelectOptions({
        prices,
        step,
        resource: key,
      });
      const defaultOption = options[0];
      return {
        ...customResource[key],
        options,
        defaultOption,
        key,
        dataId: `option-custom-${toKebabCase(key)}`,
      };
    });
  }, [customResource]);

  const renderLabelOption = (label) => (
    <Typography className="text-label">{label}</Typography>
  );

  const renderTooltipOption = (tooltip, key) => {
    if (tooltip && RESOURCES_TYPE.CREDITS === key) {
      return (
        <Typography
          component="span"
          sx={{ fontSize: '14px' }}
          style={{ textAlign: 'left' }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(tooltip, {
              allowedTags: ['ul', 'li'],
              allowedAttributes: {
                li: ['style'],
                ul: ['style'],
              },
            }),
          }}
        />
      );
    }
    return null;
  };

  const formatPrice = (vndPrice, usdPrice) => {
    if (isVietNam) {
      const unitPrice = "<span style='text-decoration: underline;'>đ</span>";
      return `${unitPrice}${vndPrice?.toLocaleString(language)}`;
    }
    return `$${usdPrice?.toLocaleString(language)}`;
  };

  const handleSelectOption = (key, option) => {
    const { value } = option || {};
    if (onPriceChange) onPriceChange(key, value);
  };

  const getInitializeOptions = () => {
    const initializePrices = customResources.reduce((initialOption, option) => {
      const { key, defaultOption } = option || {};
      const { value } = defaultOption || {};
      return {
        ...initialOption,
        [key]: value,
      };
    }, {});

    return initializePrices;
  };

  useEffect(() => {
    if (customResources?.length) {
      const initializePrices = getInitializeOptions();
      if (onInitializePrices) onInitializePrices(initializePrices);
    }
  }, [customResources]);

  const handleShowPopup = (tooltip) => {
    setTooltipDetail(tooltip);
    setOpenPopup(true);
  };

  const renderCustomResources = () =>
    customResources?.map((resource) => {
      const {
        defaultOption,
        options,
        title,
        description,
        tooltip,
        key,
        dataId,
      } = resource || {};
      const commonProps = {
        headerOption: renderLabelOption(title[language]),
        dataId,
        dataKey: resource?.key,
        infoOption: !!description && (
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t(description[language], {
                  pricePerUnit: formatPrice(
                    defaultOption.vndPricePerUnit,
                    defaultOption?.usdPricePerUnit,
                  ),
                }),
                {
                  allowedTags: ['span'],
                  allowedAttributes: {
                    span: ['style'],
                  },
                },
              ),
            }}
          />
        ),
        inputType: 'number',
        defaultValue: defaultOption,
        onChange: (option) => handleSelectOption(key, option),
        detailInfoOption: renderTooltipOption(
          tooltip?.[language],
          resource?.key,
        ),
        options,
        handleClickShowPopup: () => handleShowPopup(tooltip[language]),
        note: pkg?.note && [RESOURCES_TYPE.CREDITS].includes(key) && (
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                language === LANGUAGE.VN ? note[language] : note[LANGUAGE.EN],
              ),
            }}
          />
        ),
      };

      return (
        <div key={resource?.key} className="block-resource">
          {mergePackageApiToStudioEnabled ? (
            <NewCustomOptions {...commonProps} />
          ) : (
            <CustomOptions
              {...commonProps}
              showInfoOptionBottom={[
                RESOURCES_TYPE.VOICE_CLONINGS,
                RESOURCES_TYPE.DEVICES,
              ].includes(key)}
              showInfoOptionTop={[RESOURCES_TYPE.CREDITS].includes(key)}
            />
          )}
        </div>
      );
    });

  return (
    <StyledCustomResources>
      <div className="container-custom-resources">
        {renderCustomResources()}
      </div>
      <NotificationDialogV3
        name={t('detailedInformation')}
        title={t('detailedInformation')}
        description={sanitizeHtml(tooltipDetail, {
          allowedTags: ['ul', 'li'],
          allowedAttributes: {
            li: ['style'],
            ul: ['style'],
          },
        })}
        open={openPopup && isMobile}
        onClose={() => setOpenPopup(false)}
        variant="null"
        fitWidth
        actionComponent={
          <Button
            onClick={() => setOpenPopup(false)}
            className="btn-close-popup"
            variant="contained"
          >
            {t('gotIt')}
          </Button>
        }
      />
    </StyledCustomResources>
  );
};

export default CustomResources;
